/**
 * Federal Capture Loop Tab Component
 * 
 * Game Theory-driven federal prime capture UI
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  TrendingUp as TrendingUpIcon,
  Handshake as HandshakeIcon,
  Gavel as GavelIcon,
  Folder as FolderIcon,
  Speed as SpeedIcon,
  Add as AddIcon,
  PlayArrow as PlayIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ContentCopy as CopyIcon,
  Schedule as ScheduleIcon,
  LinkedIn as LinkedInIcon,
  Rocket as RocketIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import federalCaptureService, {
  Prime,
  PrimeContact,
  OutreachSequence,
  Meeting,
  TeamingOpportunity,
  BidOpportunity,
  FederalCaptureDashboard,
  OutreachPerformance,
  EmailTemplate,
  CallScript,
} from '../../services/federalCaptureService';

// Status Badge Component
const StatusBadge: React.FC<{ status: string; size?: 'small' | 'medium' }> = ({ status, size = 'small' }) => {
  const colorMap: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
    cold: 'default',
    warm: 'info',
    engaged: 'warning',
    partner: 'success',
    identified: 'info',
    analyzing: 'warning',
    active: 'success',
    nda_sent: 'info',
    nda_signed: 'success',
    vendor_approved: 'success',
    in_progress: 'warning',
    submitted: 'info',
    won: 'success',
    lost: 'error',
  };
  
  return (
    <Chip
      label={status.replace(/_/g, ' ').toUpperCase()}
      color={colorMap[status] || 'default'}
      size={size}
      sx={{ fontWeight: 600, fontSize: '0.7rem' }}
    />
  );
};

// PAS Score Display
const PasScore: React.FC<{ score: number; passesGates?: boolean }> = ({ score, passesGates }) => {
  const getColor = (s: number) => {
    if (s >= 0.7) return 'success.main';
    if (s >= 0.5) return 'warning.main';
    if (s >= 0.3) return 'info.main';
    return 'error.main';
  };
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <CircularProgress
        variant="determinate"
        value={score * 100}
        size={36}
        sx={{ color: getColor(score) }}
      />
      <Box>
        <Typography variant="body2" fontWeight={600}>
          {(score * 100).toFixed(0)}
        </Typography>
        {passesGates !== undefined && (
          <Chip
            label={passesGates ? 'GATES ✓' : 'BLOCKED'}
            size="small"
            color={passesGates ? 'success' : 'error'}
            sx={{ height: 18, fontSize: '0.6rem' }}
          />
        )}
      </Box>
    </Box>
  );
};

// Sub-tab panel
interface SubTabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function SubTabPanel({ children, value, index }: SubTabPanelProps) {
  return (
    <div hidden={value !== index} style={{ paddingTop: 16 }}>
      {value === index && children}
    </div>
  );
}

const FederalCaptureTab: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generatingLeads, setGeneratingLeads] = useState(false);
  const [subTab, setSubTab] = useState(0);
  const [dashboard, setDashboard] = useState<FederalCaptureDashboard | null>(null);
  const [primes, setPrimes] = useState<Prime[]>([]);
  const [sequences, setSequences] = useState<OutreachSequence[]>([]);
  const [teamingOpps, setTeamingOpps] = useState<TeamingOpportunity[]>([]);
  const [bidOpps, setBidOpps] = useState<BidOpportunity[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [callScripts, setCallScripts] = useState<CallScript[]>([]);
  const [performance, setPerformance] = useState<OutreachPerformance | null>(null);
  
  // Dialog states
  const [addPrimeOpen, setAddPrimeOpen] = useState(false);
  const [primeForm, setPrimeForm] = useState<Partial<Prime>>({
    name: '',
    size: 'mid',
    uses_subcontractors: true,
    has_partner_program: false,
    naics_codes: [],
    agencies: [],
    contract_vehicles: [],
  });
  
  const [selectedPrime, setSelectedPrime] = useState<Prime | null>(null);
  const [primeContacts, setPrimeContacts] = useState<PrimeContact[]>([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dashData, primesData, seqData, teamData, bidData, perfData, emailData, callData] = await Promise.all([
        federalCaptureService.getDashboard(),
        federalCaptureService.getPrimes({ limit: 50 }),
        federalCaptureService.getSequences(),
        federalCaptureService.getTeamingOpportunities(),
        federalCaptureService.getBidOpportunities(),
        federalCaptureService.getOutreachPerformance(),
        federalCaptureService.getColdEmailTemplates(),
        federalCaptureService.getColdCallScripts(),
      ]);
      setDashboard(dashData);
      setPrimes(primesData);
      setSequences(seqData);
      setTeamingOpps(teamData);
      setBidOpps(bidData);
      setPerformance(perfData);
      setEmailTemplates(emailData);
      setCallScripts(callData);
    } catch (err) {
      console.error('Failed to load federal capture data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPrime = async () => {
    try {
      await federalCaptureService.ingestPrime(primeForm);
      setAddPrimeOpen(false);
      setPrimeForm({
        name: '',
        size: 'mid',
        uses_subcontractors: true,
        has_partner_program: false,
        naics_codes: [],
        agencies: [],
        contract_vehicles: [],
      });
      loadData();
    } catch (err) {
      console.error('Failed to add prime:', err);
    }
  };

  const handleSelectPrime = async (prime: Prime) => {
    setSelectedPrime(prime);
    try {
      const contacts = await federalCaptureService.getContacts(prime.id);
      setPrimeContacts(contacts);
    } catch (err) {
      console.error('Failed to load contacts:', err);
    }
  };

  const handleLaunchSequence = async (sequenceId: string, primeId: string, contactId: string | null) => {
    try {
      await federalCaptureService.launchSequence(sequenceId, primeId, contactId, 'system');
      loadData();
    } catch (err) {
      console.error('Failed to launch sequence:', err);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Dashboard Summary */}
      {/* Quick Action: Generate Federal Contractor Leads */}
      <Paper sx={{ 
        p: 2, 
        mb: 3, 
        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
        border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={600} color="success.main">
              <RocketIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Generate Federal Prime Contractor Leads
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use REV108 to discover federal contractors in major government hubs (DC, Virginia, Maryland)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={generatingLeads ? <CircularProgress size={20} color="inherit" /> : <RocketIcon />}
              disabled={generatingLeads}
              onClick={async () => {
                setGeneratingLeads(true);
                try {
                  // Call REV108 Prime API to generate leads
                  const response = await api.post('/stratagem/rev108-primes/search', {
                    focusOnFederalHubs: true,
                    targetCount: 50,
                    minEmployeeCount: 25,
                  });
                  if (response.data.success) {
                    alert(`Lead generation started! Job ID: ${response.data.data?.id || 'Created'}. Check REV108 page for results.`);
                    navigate('/rev108');
                  }
                } catch (err: any) {
                  console.error('Failed to generate leads:', err);
                  alert(`Error: ${err.response?.data?.error || err.message || 'Failed to start lead generation'}`);
                } finally {
                  setGeneratingLeads(false);
                }
              }}
              sx={{ 
                px: 4,
                boxShadow: '0 4px 14px rgba(76, 175, 80, 0.4)',
              }}
            >
              {generatingLeads ? 'Starting...' : 'Generate Federal Leads Now'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/leads?source=rev108')}
            >
              View Existing Leads
            </Button>
          </Box>
        </Box>
      </Paper>
      
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={2}>
          <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <BusinessIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>{dashboard?.primes.total || 0}</Typography>
              <Typography variant="caption" color="text.secondary">PRIMES</Typography>
              <Typography variant="body2" color="text.secondary">
                {dashboard?.primes.usingSubs || 0} use subs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.info.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <PersonIcon sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>{dashboard?.contacts.total || 0}</Typography>
              <Typography variant="caption" color="text.secondary">CONTACTS</Typography>
              <Typography variant="body2" color="text.secondary">
                {dashboard?.contacts.warm || 0} warm
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <EmailIcon sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>{dashboard?.outreach.last30Days || 0}</Typography>
              <Typography variant="caption" color="text.secondary">OUTREACH (30d)</Typography>
              <Typography variant="body2" color="text.secondary">
                {dashboard?.outreach.meetingsBooked || 0} meetings
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <HandshakeIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>{dashboard?.teaming.active || 0}</Typography>
              <Typography variant="caption" color="text.secondary">ACTIVE TEAMING</Typography>
              <Typography variant="body2" color="text.secondary">
                {dashboard?.teaming.total || 0} total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.error.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <GavelIcon sx={{ fontSize: 32, color: 'error.main', mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>{dashboard?.bids.inPipeline || 0}</Typography>
              <Typography variant="caption" color="text.secondary">BIDS IN PIPELINE</Typography>
              <Typography variant="body2" color="text.secondary">
                ${((dashboard?.bids.pipelineValue || 0) / 1000000).toFixed(1)}M
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card variant="outlined" sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <FolderIcon sx={{ fontSize: 32, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>{dashboard?.artifacts.shareable || 0}</Typography>
              <Typography variant="caption" color="text.secondary">PROOF ARTIFACTS</Typography>
              <Typography variant="body2" color="text.secondary">
                {dashboard?.artifacts.withReferences || 0} refs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sub-tabs for Federal Capture */}
      <Tabs
        value={subTab}
        onChange={(_, v) => setSubTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab icon={<BusinessIcon />} iconPosition="start" label="Primes" />
        <Tab icon={<EmailIcon />} iconPosition="start" label="Outreach" />
        <Tab icon={<HandshakeIcon />} iconPosition="start" label="Teaming" />
        <Tab icon={<GavelIcon />} iconPosition="start" label="Bids" />
        <Tab icon={<SpeedIcon />} iconPosition="start" label="Templates" />
      </Tabs>

      {/* Sub-tab 0: Primes */}
      <SubTabPanel value={subTab} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            PAS Scoring: (Win × Fit × Access × Delivery) ÷ (Friction × Cost × Risk) × Velocity
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddPrimeOpen(true)}
          >
            Add Prime
          </Button>
        </Box>
        
        <Grid container spacing={2}>
          {/* Primes List */}
          <Grid item xs={12} md={selectedPrime ? 6 : 12}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Prime</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Uses Subs</TableCell>
                    <TableCell>Program</TableCell>
                    <TableCell>PAS</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {primes.map((prime) => (
                    <TableRow
                      key={prime.id}
                      hover
                      selected={selectedPrime?.id === prime.id}
                      onClick={() => handleSelectPrime(prime)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Box>
                          <Typography fontWeight={600}>{prime.name}</Typography>
                          {prime.cage_code && (
                            <Typography variant="caption" color="text.secondary">
                              CAGE: {prime.cage_code}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={prime.size.toUpperCase()}
                          size="small"
                          color={prime.size === 'large' ? 'success' : prime.size === 'mid' ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {prime.uses_subcontractors ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <WarningIcon color="error" fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        {prime.has_partner_program ? (
                          <Chip label="YES" size="small" color="success" />
                        ) : (
                          <Chip label="NO" size="small" color="default" />
                        )}
                      </TableCell>
                      <TableCell>
                        {prime.pas_score !== undefined ? (
                          <PasScore score={prime.pas_score} passesGates={prime.passes_all_gates} />
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Launch Outreach">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (sequences.length > 0) {
                                handleLaunchSequence(sequences[0].id, prime.id, null);
                              }
                            }}
                          >
                            <PlayIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          
          {/* Prime Detail */}
          {selectedPrime && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">{selectedPrime.name}</Typography>
                    <IconButton size="small" onClick={() => setSelectedPrime(null)}>
                      ×
                    </IconButton>
                  </Box>
                  
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    {selectedPrime.cage_code && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">CAGE</Typography>
                        <Typography>{selectedPrime.cage_code}</Typography>
                      </Grid>
                    )}
                    {selectedPrime.uei_number && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">UEI</Typography>
                        <Typography>{selectedPrime.uei_number}</Typography>
                      </Grid>
                    )}
                    {selectedPrime.website && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Website</Typography>
                        <Typography>
                          <a href={selectedPrime.website} target="_blank" rel="noopener noreferrer">
                            {selectedPrime.website}
                          </a>
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                  
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Contacts ({primeContacts.length})</Typography>
                  {primeContacts.map((contact) => (
                    <Box
                      key={contact.id}
                      sx={{
                        p: 1,
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: 'background.default',
                        border: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography fontWeight={600}>
                            {contact.first_name} {contact.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {contact.title} • {contact.role_type.toUpperCase()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <StatusBadge status={contact.relationship_status} />
                          {contact.is_decision_maker && (
                            <Chip label="DM" size="small" color="warning" />
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        {contact.email && (
                          <Tooltip title={contact.email}>
                            <IconButton size="small">
                              <EmailIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {contact.phone && (
                          <Tooltip title={contact.phone}>
                            <IconButton size="small">
                              <PhoneIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {contact.linkedin_url && (
                          <Tooltip title="LinkedIn">
                            <IconButton
                              size="small"
                              onClick={() => window.open(contact.linkedin_url, '_blank')}
                            >
                              <LinkedInIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </SubTabPanel>

      {/* Sub-tab 1: Outreach */}
      <SubTabPanel value={subTab} index={1}>
        <Grid container spacing={2}>
          {/* Performance Stats */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Outreach Performance (30 days)</Typography>
                {performance && (
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Email Sent</Typography>
                      <Typography variant="h5">{performance.email.sent}</Typography>
                      <Typography variant="caption" color="success.main">
                        {performance.email.open_rate} open • {performance.email.reply_rate} reply
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Calls Made</Typography>
                      <Typography variant="h5">{performance.calls.attempted}</Typography>
                      <Typography variant="caption" color="success.main">
                        {performance.calls.connect_rate} connect
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Meetings</Typography>
                      <Typography variant="h5">{performance.meetings.scheduled}</Typography>
                      <Typography variant="caption" color="success.main">
                        {performance.meetings.booking_rate} rate
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          {/* Active Sequences */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Active Sequences</Typography>
                {sequences.map((seq) => (
                  <Box
                    key={seq.id}
                    sx={{
                      p: 1.5,
                      mb: 1,
                      borderRadius: 1,
                      bgcolor: 'background.default',
                      border: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography fontWeight={600}>{seq.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {seq.total_steps} steps • {seq.channel} • {seq.target_type}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2">
                          {seq.performance_stats.meetings} meetings
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          from {seq.performance_stats.sent} sent
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
                {sequences.length === 0 && (
                  <Alert severity="info">No sequences configured yet</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </SubTabPanel>

      {/* Sub-tab 2: Teaming */}
      <SubTabPanel value={subTab} index={2}>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Opportunity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>NDA</TableCell>
                <TableCell>Onboarding</TableCell>
                <TableCell>Scope Alignment</TableCell>
                <TableCell>Est. Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teamingOpps.map((opp) => (
                <TableRow key={opp.id}>
                  <TableCell>
                    <Typography fontWeight={600}>{opp.opportunity_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={opp.status} />
                  </TableCell>
                  <TableCell>
                    {opp.nda_signed_at ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <ScheduleIcon color="disabled" fontSize="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={opp.onboarding_pct}
                        sx={{ flexGrow: 1 }}
                      />
                      <Typography variant="caption">{opp.onboarding_pct}%</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={opp.scope_alignment_pct}
                        color="secondary"
                        sx={{ flexGrow: 1 }}
                      />
                      <Typography variant="caption">{opp.scope_alignment_pct}%</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {opp.estimated_value ? `$${(opp.estimated_value / 1000).toFixed(0)}K` : '-'}
                  </TableCell>
                </TableRow>
              ))}
              {teamingOpps.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Alert severity="info">No teaming opportunities yet. Launch outreach to generate leads.</Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SubTabPanel>

      {/* Sub-tab 3: Bids */}
      <SubTabPanel value={subTab} index={3}>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Bid</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Agency</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell>Win %</TableCell>
                <TableCell>Due</TableCell>
                <TableCell>Our Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bidOpps.map((bid) => (
                <TableRow key={bid.id}>
                  <TableCell>
                    <Typography fontWeight={600}>{bid.title}</Typography>
                    {bid.solicitation_number && (
                      <Typography variant="caption" color="text.secondary">
                        {bid.solicitation_number}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={bid.bid_type.toUpperCase()} size="small" />
                  </TableCell>
                  <TableCell>{bid.agency || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={bid.our_role === 'subcontractor' ? 'SUB' : 'PRIME'}
                      size="small"
                      color={bid.our_role === 'subcontractor' ? 'info' : 'success'}
                    />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={bid.stage} />
                  </TableCell>
                  <TableCell>
                    <Typography
                      color={bid.win_probability >= 50 ? 'success.main' : 'text.secondary'}
                      fontWeight={600}
                    >
                      {bid.win_probability}%
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {bid.due_date ? new Date(bid.due_date).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    {bid.our_scope_value ? `$${(bid.our_scope_value / 1000).toFixed(0)}K` : '-'}
                  </TableCell>
                </TableRow>
              ))}
              {bidOpps.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Alert severity="info">No bid opportunities yet. Build teaming relationships first.</Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SubTabPanel>

      {/* Sub-tab 4: Templates */}
      <SubTabPanel value={subTab} index={4}>
        <Grid container spacing={2}>
          {/* Email Templates */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Cold Email Templates</Typography>
                {emailTemplates.map((template) => (
                  <Accordion key={template.id}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography fontWeight={600}>{template.name}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Subject: {template.subject}
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, bgcolor: 'background.default', whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                      >
                        {template.body}
                      </Paper>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Variables: {template.variables.join(', ')}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          startIcon={<CopyIcon />}
                          onClick={() => copyToClipboard(template.body)}
                        >
                          Copy
                        </Button>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>
          </Grid>
          
          {/* Call Scripts */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Cold Call Scripts</Typography>
                {callScripts.map((script) => (
                  <Accordion key={script.id}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography fontWeight={600}>{script.name}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="primary.main">Opening</Typography>
                        <Typography variant="body2">{script.opening}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="primary.main">Bridge</Typography>
                        <Typography variant="body2">{script.bridge}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="primary.main">Value Prop</Typography>
                        <Typography variant="body2">{script.value_prop}</Typography>
                      </Box>
                      {script.qualifying_questions && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="primary.main">Qualifying Questions</Typography>
                          <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {script.qualifying_questions.map((q, i) => (
                              <li key={i}><Typography variant="body2">{q}</Typography></li>
                            ))}
                          </ul>
                        </Box>
                      )}
                      <Box>
                        <Typography variant="subtitle2" color="success.main">Close</Typography>
                        <Typography variant="body2">{script.close}</Typography>
                      </Box>
                      {script.objection_handling && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" color="warning.main">Objection Handling</Typography>
                          {Object.entries(script.objection_handling).map(([key, response]) => (
                            <Box key={key} sx={{ mt: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                "{key.replace(/_/g, ' ')}"
                              </Typography>
                              <Typography variant="body2">{response}</Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </SubTabPanel>

      {/* Add Prime Dialog */}
      <Dialog open={addPrimeOpen} onClose={() => setAddPrimeOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Federal Prime</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name"
                value={primeForm.name}
                onChange={(e) => setPrimeForm({ ...primeForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="CAGE Code"
                value={primeForm.cage_code || ''}
                onChange={(e) => setPrimeForm({ ...primeForm, cage_code: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="UEI Number"
                value={primeForm.uei_number || ''}
                onChange={(e) => setPrimeForm({ ...primeForm, uei_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website"
                value={primeForm.website || ''}
                onChange={(e) => setPrimeForm({ ...primeForm, website: e.target.value })}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                select
                label="Size"
                value={primeForm.size}
                onChange={(e) => setPrimeForm({ ...primeForm, size: e.target.value as any })}
                SelectProps={{ native: true }}
              >
                <option value="small">Small</option>
                <option value="mid">Mid-size</option>
                <option value="large">Large</option>
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                select
                label="Uses Subcontractors"
                value={primeForm.uses_subcontractors ? 'yes' : 'no'}
                onChange={(e) => setPrimeForm({ ...primeForm, uses_subcontractors: e.target.value === 'yes' })}
                SelectProps={{ native: true }}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                select
                label="Has Partner Program"
                value={primeForm.has_partner_program ? 'yes' : 'no'}
                onChange={(e) => setPrimeForm({ ...primeForm, has_partner_program: e.target.value === 'yes' })}
                SelectProps={{ native: true }}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={primeForm.notes || ''}
                onChange={(e) => setPrimeForm({ ...primeForm, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPrimeOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddPrime} disabled={!primeForm.name}>
            Add Prime
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FederalCaptureTab;
